import { IPaginationParams } from 'APIPackage/MainModule/interfaces/IPaginationParams';
import { createPaginationParams } from 'APIPackage/MainModule/common/createPaginationParams';
import { getServiceBaseURL } from 'APIPackage/MainModule/common/getServiceBaseURL';
import { APIServiceEnum } from 'APIPackage/MainModule/enums/APIServiceEnum';
import { apiGet } from 'APIPackage/MainModule/common/apiTools';

import { IKnoOrganisationsChildrenResponse } from '../interfaces/IKnoOrganisationsChildrenResponse';
import { IKnoOrganization } from '../interfaces/IKnoOrganization';
import { IKnoLevel } from '../interfaces/IKnoLevel';

interface IFetchKnoOrganisationsChildren extends IPaginationParams {
  adminLevel: number;
  id?: string;
}

interface IFetchKnoOrganisationsSearch extends IPaginationParams {
  query: string;
  adminLevel?: string;
  searchCriteria?: string;
}

interface IFetchKnoErvkOrganisationsChildren extends IPaginationParams {
  knoLevelCode: string | null;
  id?: string;
}

interface IFetchKnoErvkOrganisationsSearch extends IPaginationParams {
  query: string;
  knoLevelCode: string | null;
  searchCriteria?: string;
}

export async function fetchKnoOrganisationsChildren({
  adminLevel,
  id,
  ...paginationParams
}: IFetchKnoOrganisationsChildren): Promise<IKnoOrganisationsChildrenResponse> {
  const queryParams = new URLSearchParams();

  queryParams.append('adminLevel', adminLevel.toString());
  if (id) {
    queryParams.append('organizationId', id.toString());
  }
  const params = createPaginationParams(paginationParams);

  return apiGet<IKnoOrganisationsChildrenResponse>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/catalogs/kno-organizations/children?${queryParams}&${params}`,
  );
}

export async function fetchKnoOrganisationsSearch({
  query,
  adminLevel,
  searchCriteria = 'name',
  ...paginationParams
}: IFetchKnoOrganisationsSearch): Promise<IKnoOrganisationsChildrenResponse> {
  const queryParams = new URLSearchParams();

  queryParams.append('query', query);
  if (adminLevel) {
    queryParams.append('adminLevel', adminLevel.toString());
  }
  queryParams.append('searchCriteria', searchCriteria);
  const params = createPaginationParams(paginationParams);

  return apiGet<IKnoOrganisationsChildrenResponse>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/catalogs/kno-organizations/search?${queryParams}&${params}`,
  );
}

export async function fetchKnoOrganisationsSearchById(id: string): Promise<IKnoOrganization> {
  return apiGet<IKnoOrganization>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/catalogs/kno-organizations/${id}`);
}

export async function fetchKnoErvkOrganizationsSearch({
  query,
  ...paginationParams
}: IFetchKnoOrganisationsSearch): Promise<IKnoOrganisationsChildrenResponse> {
  const queryParams = new URLSearchParams();

  queryParams.append('query', query);

  const params = createPaginationParams(paginationParams);

  return apiGet<IKnoOrganisationsChildrenResponse>(
    `/private/api/dictionaries/kno-organizations/search?${queryParams}&${params}`,
  );
}

export async function fetchKnoErvkOrganizationsSearchByRelevance({
  query,
  ...paginationParams
}: IFetchKnoOrganisationsSearch): Promise<IKnoOrganisationsChildrenResponse> {
  const queryParams = new URLSearchParams();

  queryParams.append('query', query);

  const params = createPaginationParams(paginationParams);

  return apiGet<IKnoOrganisationsChildrenResponse>(
    `${getServiceBaseURL(
      APIServiceEnum.BASE_SERVICE,
      false,
    )}/dictionaries/kno-organizations/search-by-relevance?${queryParams}&${params}`,
  );
}

export async function fetchKnoOrganisationsIsPrivilegesKno(ogrn: string) {
  return apiGet<boolean>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/catalogs/kno-organizations/${ogrn}/is-privileges-kno`,
  );
}

export async function fetchKnoLevels(): Promise<IKnoLevel[]> {
  return apiGet<IKnoLevel[]>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/catalogs/erknm/kno-levels`);
}

export async function fetchKnoErvkOrganisationsSearch({
  query,
  knoLevelCode,
  searchCriteria = 'name',
  ...paginationParams
}: IFetchKnoErvkOrganisationsSearch): Promise<IKnoOrganisationsChildrenResponse> {
  const queryParams = new URLSearchParams();

  queryParams.append('query', query);
  if (knoLevelCode) {
    queryParams.append('knoLevelCode', knoLevelCode);
  }
  queryParams.append('searchCriteria', searchCriteria);
  const params = createPaginationParams(paginationParams);

  return apiGet<IKnoOrganisationsChildrenResponse>(
    `${getServiceBaseURL(
      APIServiceEnum.BASE_SERVICE,
    )}/catalogs/kno-ervk-organizations/search_with_agency_type?${queryParams}&${params}`,
  );
}

export async function fetchKnoErvkOrganisationsChildrenWithAdminLevel({
  knoLevelCode,
  id,
  ...paginationParams
}: IFetchKnoErvkOrganisationsChildren): Promise<IKnoOrganisationsChildrenResponse> {
  const queryParams = new URLSearchParams();

  if (knoLevelCode) {
    queryParams.append('adminLevel', knoLevelCode);
  }

  if (id) {
    queryParams.append('organizationId', id.toString());
  }
  const params = createPaginationParams(paginationParams);

  return apiGet<IKnoOrganisationsChildrenResponse>(
    `${getServiceBaseURL(
      APIServiceEnum.BASE_SERVICE,
    )}/dictionaries/kno-organizations/children?${queryParams}&${params}`,
  );
}

export async function fetchKnoErvkOrganisationsChildrenWithKnoLevelCode({
  knoLevelCode,
  id,
  ...paginationParams
}: IFetchKnoErvkOrganisationsChildren): Promise<IKnoOrganisationsChildrenResponse> {
  const queryParams = new URLSearchParams();

  if (knoLevelCode) {
    queryParams.append('knoLevelCode', knoLevelCode);
  }

  if (id) {
    queryParams.append('organizationId', id.toString());
  }
  const params = createPaginationParams(paginationParams);

  return apiGet<IKnoOrganisationsChildrenResponse>(
    `${getServiceBaseURL(
      APIServiceEnum.BASE_SERVICE,
    )}/catalogs/kno-ervk-organizations/children?${queryParams}&${params}`,
  );
}
